import { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { Identity, AnonymousIdentity } from '@dfinity/agent';
import { AuthClient } from '@dfinity/auth-client';

const II_CANISTER_ID = import.meta.env.VITE_INTERNET_IDENTITY_CANISTER_ID || 'rdmx6-jaaaa-aaaaa-aaadq-cai';

type LoginStatus = 'idle' | 'initializing' | 'logging-in' | 'authenticated' | 'error';

interface InternetIdentityContextType {
  identity: Identity | null;
  login: () => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loginStatus: LoginStatus;
}

const InternetIdentityContext = createContext<InternetIdentityContextType | undefined>(undefined);

export function InternetIdentityProvider({ children }: { children: ReactNode }) {
  // Check if we're in local development - check hostname FIRST to avoid misclassifying mainnet
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.includes('localhost');
  const isLocalNetwork = import.meta.env.VITE_DFX_NETWORK !== 'ic';
  // Only treat as local if actually on localhost OR explicitly not on mainnet
  const isLocal = isLocalhost || (isLocalNetwork && !window.location.hostname.includes('.ic0.app') && !window.location.hostname.includes('.icp0.io'));
  
  const [identity, setIdentity] = useState<Identity | null>(new AnonymousIdentity());
  const [isAuthenticated, setIsAuthenticated] = useState(isLocal);
  const [loginStatus, setLoginStatus] = useState<LoginStatus>(isLocal ? 'authenticated' : 'initializing');
  const authClientRef = useRef<AuthClient | null>(null);
  const isInitializingRef = useRef(false);

  // Initialize AuthClient and check for existing session
  useEffect(() => {
    // For local development, skip II auth
    if (isLocal) {
      setIdentity(new AnonymousIdentity());
      setIsAuthenticated(true);
      setLoginStatus('authenticated');
      return;
    }

    // Prevent multiple initializations
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;

    const initializeAuth = async () => {
      try {
        setLoginStatus('initializing');
        
        // Create AuthClient
        const authClient = await AuthClient.create({
          idleOptions: {
            disableIdle: true, // Disable idle timeout for now
          },
        });
        authClientRef.current = authClient;

        // Check if already authenticated (this also handles callback if present in URL)
        const isAuth = await authClient.isAuthenticated();
        if (isAuth) {
          const authIdentity = authClient.getIdentity();
          setIdentity(authIdentity);
          setIsAuthenticated(true);
          setLoginStatus('authenticated');
          console.log('[II] Authenticated:', authIdentity.getPrincipal().toString());
          
          // Clean up URL hash if present (from II callback)
          if (window.location.hash.includes('#authorize=')) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        } else {
          // Not authenticated - ready for login
          setIdentity(new AnonymousIdentity());
          setIsAuthenticated(false);
          setLoginStatus('idle');
        }
      } catch (error) {
        console.error('[II] Initialization error:', error);
        setLoginStatus('error');
        setIdentity(new AnonymousIdentity());
        setIsAuthenticated(false);
      } finally {
        isInitializingRef.current = false;
      }
    };

    initializeAuth();
  }, [isLocal]);

  const login = async () => {
    // For local dev, just set authenticated
    if (isLocal) {
      setIsAuthenticated(true);
      setIdentity(new AnonymousIdentity());
      setLoginStatus('authenticated');
      return;
    }

    if (!authClientRef.current) {
      console.error('[II] AuthClient not initialized');
      return;
    }

    try {
      setLoginStatus('logging-in');
      
      // login() redirects to II - it doesn't return until after callback
      // The callback will be handled by the useEffect when page reloads
      authClientRef.current.login({
        identityProvider: `https://${II_CANISTER_ID}.ic0.app`,
        maxTimeToLive: BigInt(7 * 24 * 60 * 60 * 1000 * 1000 * 1000), // 7 days in nanoseconds
        onSuccess: async () => {
          // This callback runs after redirect back from II
          if (authClientRef.current) {
            const authIdentity = authClientRef.current.getIdentity();
            setIdentity(authIdentity);
            setIsAuthenticated(true);
            setLoginStatus('authenticated');
            console.log('[II] Login successful:', authIdentity.getPrincipal().toString());
            
            // Clean up URL hash
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        },
        onError: (error) => {
          console.error('[II] Login error:', error);
          setLoginStatus('error');
          setIsAuthenticated(false);
          setIdentity(new AnonymousIdentity());
        },
      });
    } catch (error) {
      console.error('[II] Login failed:', error);
      setLoginStatus('error');
      setIsAuthenticated(false);
      setIdentity(new AnonymousIdentity());
    }
  };

  const logout = async () => {
    if (isLocal) {
      setIdentity(new AnonymousIdentity());
      setIsAuthenticated(false);
      setLoginStatus('idle');
      return;
    }

    if (authClientRef.current) {
      await authClientRef.current.logout();
    }
    
    setIdentity(new AnonymousIdentity());
    setIsAuthenticated(false);
    setLoginStatus('idle');
    console.log('[II] Logged out');
  };

  return (
    <InternetIdentityContext.Provider value={{
      identity,
      login,
      logout,
      isAuthenticated,
      loginStatus,
    }}>
      {children}
    </InternetIdentityContext.Provider>
  );
}

export function useInternetIdentity() {
  const context = useContext(InternetIdentityContext);
  if (!context) {
    throw new Error('useInternetIdentity must be used within InternetIdentityProvider');
  }
  return context;
}
